/**
 * Custom hook for intelligent draft review search with React Query caching.
 *
 * This hook provides intent-based search for Draft Review Mode:
 * - Extracts intent from natural language queries
 * - Searches comments, track changes, AND draft label sections
 * - Returns LLM-scored and ranked results
 *
 * Used by Draft Review Mode in SearchPage when a search query is entered.
 */
import { useQuery } from '@tanstack/react-query';
import { authApi } from '../services/authApi';
import { DraftReviewSearchResponse, OutcomeStatus } from '../types';

interface DraftReviewSearchOptions {
  /** Natural language search query */
  query: string;
  /** Filter by edit type */
  edit_type?: 'comment' | 'deletion' | 'insertion';
  /** Filter by outcome status */
  outcome_status?: OutcomeStatus;
  /** Filter by author name */
  author?: string;
  /** Filter by specific drug ID */
  drug_id?: number;
  /** Filter by set_id */
  set_id?: string;
  /** Filter by date range start (ISO format) */
  date_from?: string;
  /** Filter by date range end (ISO format) */
  date_to?: string;
  /** Maximum results to return */
  limit?: number;
  /** Pagination offset */
  offset?: number;
}

/**
 * Hook for intelligent draft review search.
 *
 * Unlike the basic useDraftCommentsQuery, this hook:
 * - Requires a search query (doesn't fetch all results by default)
 * - Uses intent extraction to understand the query
 * - Returns results with actual relevance scores (0-10)
 * - Searches both comments AND draft sections
 *
 * Cache behavior:
 * - Results are cached for 2 minutes (staleTime)
 * - Query key includes the search query and all filters
 * - Disabled when query is empty or whitespace-only
 *
 * @param options - Search options including required query
 * @param enabled - Whether the query should run (default: true, but requires non-empty query)
 * @returns React Query result with DraftReviewSearchResponse
 */
export function useDraftReviewSearch(
  options: DraftReviewSearchOptions,
  enabled: boolean = true
) {
  // Only enable if query is non-empty
  const hasValidQuery = options.query?.trim().length > 0;
  const shouldEnable = enabled && hasValidQuery;

  return useQuery<DraftReviewSearchResponse>({
    queryKey: ['draft-review-search', options],
    queryFn: async () => {
      // Build request params, only including defined values
      const params: Parameters<typeof authApi.searchDraftReview>[0] = {
        query: options.query,
        limit: options.limit ?? 50,
        offset: options.offset ?? 0,
      };

      // Conditionally add optional params (avoids undefined values)
      if (options.edit_type) params.edit_type = options.edit_type;
      if (options.outcome_status) params.outcome_status = options.outcome_status;
      if (options.author) params.author = options.author;
      if (options.drug_id !== undefined) params.drug_id = options.drug_id;
      if (options.set_id) params.set_id = options.set_id;
      if (options.date_from) params.date_from = options.date_from;
      if (options.date_to) params.date_to = options.date_to;

      const response = await authApi.searchDraftReview(params);
      return response;
    },
    enabled: shouldEnable,
    // Keep data fresh for 2 minutes
    staleTime: 2 * 60 * 1000,
    // Keep in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Don't refetch on mount if data is still fresh
    refetchOnMount: false,
  });
}

export default useDraftReviewSearch;
